<template>
  <div class="color-picker-wrapper" :class="{
    'has-warning': !hasValidation,
    'has-success': hasValidation,
    'is-disabled': isDisabled
  }">
    <!-- Label -->
    <label v-if="label && label !== ''" :for="elemId" class="color-picker-label">
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- ColorPicker swatch + hex text input -->
    <div class="color-picker-controls">
      <ColorPicker
        v-model="internalValue"
        :id="elemId"
        :disabled="isDisabled"
        :invalid="isRequired && !hasValidation"
        :inline="inline"
        :format="format"
        @update:modelValue="handleUpdate"
      />
      <InputText
        v-if="!inline"
        v-model="hexInput"
        class="color-picker-hex"
        :disabled="isDisabled"
        :invalid="!isHexInputValid || (isRequired && !hasValidation)"
        :maxlength="7"
        placeholder="#3c8dbc"
        :data-testid="elemId ? `${elemId}-hex-input` : 'color-picker-hex-input'"
        @update:modelValue="handleHexInput"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ColorPicker from 'primevue/colorpicker';
import InputText from 'primevue/inputtext';

const props = defineProps({
  // Legacy props (backward compatibility)
  elemId: String,
  label: String,
  validation: {
    type: Function as any,
    default: () => true
  },
  required: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  modelValue: String,
  value: String, // Keep for backward compatibility

  // PrimeVue ColorPicker props
  inline: {
    type: Boolean,
    default: false
  },
  format: {
    type: String,
    default: 'hex' // 'hex', 'rgb', 'hsb'
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
}>();

// Convert RGB string to Hex
function rgbToHex(rgb: string): string | null {
  // Extract RGB values from string like "rgb(123, 82, 171)"
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return null;

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  // Convert to hex
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return toHex(r) + toHex(g) + toHex(b);
}

// Convert any color format to hex without '#'
function normalizeColor(color: string | undefined | null): string {
  if (!color) return '3c8dbc'; // Default color without '#'

  // If it's RGB format, convert to hex
  if (color.startsWith('rgb(')) {
    const hex = rgbToHex(color);
    return hex || '3c8dbc';
  }

  // If it's hex, strip '#'
  return color.replace('#', '');
}

// Reactive data - ColorPicker expects value without '#'
const internalValue = ref<string>(normalizeColor(props.modelValue || props.value));

// Hex text field mirrors internalValue, displayed with leading '#'
const hexInput = ref<string>(`#${internalValue.value}`);
const isHexInputValid = ref<boolean>(true);

// Parse the typed hex into a 6-digit lowercase value (no '#'), or null if invalid
function parseHexInput(raw: string): string | null {
  let v = raw.trim().replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(v)) {
    v = v.split('').map((c) => c + c).join('');
  }
  return /^[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : null;
}

// Computed properties
const isRequired = computed((): boolean =>
  props.required === true || props.required === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

// Keep the hex text field in sync whenever internalValue changes (e.g. from the picker)
watch(internalValue, (newVal: string) => {
  if (parseHexInput(hexInput.value) !== newVal) {
    hexInput.value = `#${newVal}`;
  }
  isHexInputValid.value = true;
});

// Watchers
watch(() => props.modelValue, (newVal: string | undefined) => {
  internalValue.value = normalizeColor(newVal);
});

watch(() => props.value, (newVal: string | undefined) => {
  internalValue.value = normalizeColor(newVal);
});

// Handle update from PrimeVue ColorPicker - add '#' back for the model
function handleUpdate(newVal: string | null | undefined): void {
  const colorWithHash = newVal && !newVal.startsWith('#') ? `#${newVal}` : (newVal ?? '');
  emit('update:modelValue', colorWithHash);
  emit('input', colorWithHash); // Keep for backward compatibility
}

// Handle typed hex input - update the picker + model only when it parses to a valid color
function handleHexInput(raw: string | null | undefined): void {
  const parsed = parseHexInput(raw ?? '');
  isHexInputValid.value = parsed !== null;
  if (parsed !== null) {
    internalValue.value = parsed;
    handleUpdate(parsed);
  }
}
</script>

<style scoped>
.color-picker-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.color-picker-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
}

.color-picker-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.color-picker-hex {
  width: 7rem;
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.has-warning :deep(.p-colorpicker) {
  border-color: var(--p-yellow-500);
}

.has-success :deep(.p-colorpicker) {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Ensure ColorPicker button has proper styling */
:deep(.p-colorpicker-preview) {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 4px;
  border: 1px solid var(--p-inputtext-border-color);
  cursor: pointer;
}

:deep(.p-colorpicker-preview:hover) {
  border-color: var(--p-primary-color);
}
</style>
